import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import AppSelect from 'components/AppSelect';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiReq from 'core/api';
import { useApiFormErrorHandler } from 'hooks/useApiFormErrorHandler';
import { Role, RolePermission } from 'types';
import { generatePermissionsFromRoutes } from 'utils/initPermissions';
import { USE_NEW_SHELL } from 'core/flags';
import { FormPageV2, Button as ShellButton } from 'components/AppShell';

interface FormErrors {
  name?: string;
  title?: string;
  description?: string;
  permissions?: string;
}

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const availablePermissions = useMemo(() => generatePermissionsFromRoutes(), []);

  const [formData, setFormData] = useState<Role>({
    name: '',
    title: '',
    description: '',
    permissions: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const handleApiFormError = useApiFormErrorHandler();

  const loadRole = useCallback(async () => {
    try {
      setIsLoading(true);
      setApiError('');
      const response = await apiReq<Role>(`roles/${id}`, 'GET');
      if (response) {
        setFormData(response);
      } else {
        setApiError('Ошибка загрузки роли');
      }
    } catch (error) {
      setApiError('Ошибка загрузки роли');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadRole();
    }
  }, [isEdit, loadRole]);

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Код обязателен';
    } else if (formData.name.length > 10) {
      newErrors.name = 'Код не должен превышать 10 символов';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.name)) {
      newErrors.name = 'Код должен содержать только английские буквы и цифры без пробелов и знаков';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError('');

      let response;
      if (isEdit) {
        response = await apiReq<Role>(`roles/update`, 'POST', { ...formData, id: id });
      } else {
        response = await apiReq<Role>(`roles/create`, 'POST', formData);
      }

      if (response) {
        navigate('/roles');
      } else {
        setApiError('Ошибка сохранения роли');
      }
    } catch (error) {
      handleApiFormError(error, setErrors, setApiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof Role) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Очищаем ошибку при изменении поля
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const fields = (
    <Box component="form" id="role-form" onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Название роли"
        placeholder="Например Администратор"
        value={formData.title}
        onChange={handleChange('title')}
        error={Boolean(errors.title)}
        helperText={errors.title}
        margin="normal"
        required
      />
      <TextField
        fullWidth
        label="Код роли"
        placeholder="Например admin"
        value={formData.name}
        onChange={handleChange('name')}
        error={Boolean(errors.name)}
        helperText={errors.name}
        margin="normal"
        required
        inputProps={{ maxLength: 10 }}
      />
      <TextField
        fullWidth
        label="Описание"
        placeholder="Например Администратор - полный доступ к системе"
        value={formData.description}
        onChange={handleChange('description')}
        margin="normal"
        multiline
        rows={3}
      />
      <AppSelect<RolePermission, true>
        multiple
        fullWidth
        sx={{ mt: 1, mb: 0.5 }}
        options={availablePermissions}
        value={formData.permissions}
        onChange={(_, newValue) =>
          setFormData((prev) => ({ ...prev, permissions: newValue as RolePermission[] }))
        }
        getOptionLabel={(option) => option.title || option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderTags={(value, getTagProps) =>
          (value as RolePermission[]).map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option.title || option.name} size="small" {...tagProps} />
            );
          })
        }
        label="Разрешения"
      />
      {errors.permissions && <Typography color="error">{errors.permissions}</Typography>}
      {!USE_NEW_SHELL && (
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ minWidth: 120 }}>
            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/roles')} disabled={isSubmitting}>
            Отмена
          </Button>
        </Box>
      )}
    </Box>
  );

  if (USE_NEW_SHELL) {
    return (
      <FormPageV2
        title={isEdit ? 'Редактировать роль' : 'Создать роль'}
        backTo="/roles"
        isLoading={isLoading}
        errorBanner={apiError ? <Alert severity="error">{apiError}</Alert> : undefined}
        footer={
          <>
            <ShellButton variant="ghost" onClick={() => navigate('/roles')} disabled={isSubmitting}>
              Отмена
            </ShellButton>
            <ShellButton variant="primary" type="submit" form="role-form" disabled={isSubmitting}>
              {isSubmitting ? 'Сохранение…' : 'Сохранить'}
            </ShellButton>
          </>
        }
      >
        {fields}
      </FormPageV2>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
          Назад
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Редактировать роль' : 'Создать роль'}
        </Typography>
      </Box>
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}
      {fields}
    </Box>
  );
};

export default RoleForm;
